import { motion } from 'framer-motion';
import {
  Globe,
  TrendingUp,
  Target,
  Users,
  BarChart3,
  ArrowRight,
  Phone,
  Building,
  Landmark,
  Briefcase,
  FileText
} from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { companyInfo } from '../data';

const services = [
  {
    icon: TrendingUp,
    title: 'Analisis Tren Makroekonomi',
    description: 'Outlook industri berdasarkan indikator ekonomi dan pasar'
  },
  {
    icon: Target,
    title: 'Pemetaan Struktur & Kompetisi Industri',
    description: 'Analisis mendalam menggunakan Porter\'s Five Forces'
  },
  {
    icon: Globe,
    title: 'Identifikasi Peluang Pasar & Risiko Sektor',
    description: 'Mapping peluang dan risiko untuk pengambilan keputusan'
  },
  {
    icon: Users,
    title: 'Benchmarking Pelaku Industri',
    description: 'Analisis value chain dan posisi kompetitif'
  },
  {
    icon: BarChart3,
    title: 'Proyeksi Pertumbuhan Industri',
    description: 'Berdasarkan indikator ekonomi dan tren pasar'
  },
  {
    icon: FileText,
    title: 'Insight Kebijakan Ekonomi & Regulasi',
    description: 'Analisis perdagangan dan kebijakan yang berdampak pada industri'
  }
];

const suitableFor = [
  {
    icon: Globe,
    title: 'Perusahaan Ekspansi',
    description: 'Yang ingin masuk ke industri baru atau memperluas pasar lintas negara'
  },
  {
    icon: Briefcase,
    title: 'Investor & Venture Capital',
    description: 'Untuk analisis sektor potensial dan holding company'
  },
  {
    icon: Landmark,
    title: 'Lembaga Pemerintah & Asosiasi',
    description: 'Penyusunan kebijakan dan rekomendasi strategis'
  },
  {
    icon: Building,
    title: 'Korporasi',
    description: 'Yang membutuhkan pembaruan rutin data dan analisis industri'
  }
];

const packages = [
  {
    icon: Target,
    title: 'Feasibility Study & Ekspansi',
    description: 'Bisnis yang akan melakukan feasibility study atau ekspansi lintas wilayah/negara'
  },
  {
    icon: BarChart3,
    title: 'Konsultan Internal',
    description: 'Konsultan internal perusahaan yang butuh insight objektif berbasis data makro'
  },
  {
    icon: FileText,
    title: 'Peneliti & Perencana Kebijakan',
    description: 'Peneliti, lembaga riset, dan perencana kebijakan di instansi publik'
  },
  {
    icon: Globe,
    title: 'Eksportir/Importir',
    description: 'Perusahaan eksportir/imporir yang ingin menganalisis dinamika ekonomi global'
  }
];

export function IndustryEconomicAnalysis() {
  const { ref: heroRef, isInView: heroInView } = useScrollAnimation({ threshold: 0.1 });
  const { ref: servicesRef, isInView: servicesInView } = useScrollAnimation({ threshold: 0.1 });

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative pt-32 pb-20 overflow-hidden"
      >
        {/* Video Background - z-0 to stay at back */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center z-0"
        >
          <source src="/industry-analysis-bg.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Navy Blue Overlay */}
        <div className="absolute inset-0 bg-[#0a1628]/85 z-[1]" />

        <div className="relative max-w-7xl mx-auto section-padding z-[2]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-4 mb-4">
              <span className="text-6xl font-display font-bold text-white/10">09</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
              Industry & Economic Analysis
            </h1>

            <p className="text-xl text-primary-400 mb-4 leading-relaxed">
              Domestik & Global
            </p>

            <p className="text-white/60 mb-8">
              Emporium Business Solution menyediakan <strong className="text-primary-400">analisis industri dan ekonomi yang komprehensif</strong> baik untuk pasar domestik maupun global sebagai dasar pengambilan keputusan strategis bisnis dan investasi Anda.
            </p>

            <motion.a
              href={`tel:${companyInfo.phone}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
            >
              <Phone className="w-5 h-5" />
              Konsultasi Gratis
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Services & Suitable For Section */}
      <section ref={servicesRef} className="py-20 bg-dark-800/50">
        <div className="max-w-7xl mx-auto section-padding">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Layanan Kami
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Analisis industri dan ekonomi untuk pengambilan keputusan strategis
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="glass rounded-2xl p-6 hover:border-primary-500/30 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-primary-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{service.title}</h3>
                <p className="text-white/60 text-sm">{service.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Suitable For Section */}
          <div className="glass rounded-2xl p-8 md:p-12">
            <h3 className="text-xl md:text-2xl font-heading font-semibold text-white mb-6 text-center">
              Cocok Untuk
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {suitableFor.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start gap-4 p-4 bg-white/5 rounded-xl"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-primary-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-1">{item.title}</h4>
                    <p className="text-white/60 text-sm">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-10 text-center"
            >
              <motion.a
                href={`tel:${companyInfo.phone}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-2 px-8 py-4 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
              >
                <Phone className="w-5 h-5" />
                Konsultasi Gratis
                <ArrowRight className="w-5 h-5" />
              </motion.a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-20 relative overflow-hidden">
        {/* Video Background - z-0 to stay at back */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center z-0"
        >
          <source src="/industry-packages-bg.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Navy Blue Overlay */}
        <div className="absolute inset-0 bg-[#0a1628]/85 z-[1]" />

        <div className="max-w-7xl mx-auto section-padding relative z-[2]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Paket Industry & Economic Analysis
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Sesuai untuk berbagai kebutuhan analisis industri dan ekonomi
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass rounded-2xl p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center flex-shrink-0">
                    <pkg.icon className="w-6 h-6 text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">{pkg.title}</h3>
                    <p className="text-white/60 text-sm">{pkg.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-dark-800/50">
        <div className="max-w-7xl mx-auto section-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass rounded-2xl p-8 md:p-12 text-center"
          >
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center">
                <Globe className="w-6 h-6 text-primary-400" />
              </div>
            </div>

            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
              Siap Menganalisis Industri & Ekonomi?
            </h2>

            <p className="text-white/60 mb-8 max-w-2xl mx-auto">
              Dapatkan analisis industri dan ekonomi domestik maupun global untuk pengambilan keputusan strategis.
            </p>

            <motion.a
              href={`tel:${companyInfo.phone}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
            >
              <Phone className="w-5 h-5" />
              Konsultasi Gratis
              <ArrowRight className="w-5 h-5" />
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
